/**
 * Product media gallery.
 *
 * Desktop (990px and up): a static single-column stack of every slide, no
 * carousel and no thumbnail rail. Below that: two synced Embla carousels,
 * the main viewer and a horizontal thumbnail strip. The mode follows the
 * breakpoint live. Implements the `setActiveMedia(mediaId)` contract that
 * `product-info.js` calls after a variant change, so the carousel stays in
 * sync with the selected variant; in grid mode every slide is visible, so
 * the call is a no-op.
 *
 * Requires the EmblaCarousel global from `embla-carousel.min.js`.
 */
if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      /** @type {ReturnType<typeof EmblaCarousel> | null} */
      #main = null;
      #thumbButtons = [];
      #desktopQuery = window.matchMedia('(min-width: 990px)');
      /** @type {MutationObserver | null} */
      #mediaObserver = null;
      /** Grid mode: plays a slide's video only while it is on screen. */
      #viewObserver = null;
      /** Token of the latest variant swap; older in-flight swaps bail out. */
      #variantSwaps = new Map();

      #onBreakpoint = () => this.#applyMode();

      connectedCallback() {
        this.mainViewport = this.querySelector('[data-embla-main]');
        this.thumbsViewport = this.querySelector('[data-embla-thumbs]');
        this.thumbsContainer = this.querySelector('[data-embla-thumbs-container]');
        this.status = this.querySelector('[role="status"]');
        this.prevButton = this.querySelector('[data-embla-prev]');
        this.nextButton = this.querySelector('[data-embla-next]');

        if (!this.mainViewport) return;

        this.#patchEmbeddedMedia();
        this.#mediaObserver = new MutationObserver(() => this.#patchEmbeddedMedia());
        this.#mediaObserver.observe(this.mainViewport, { childList: true, subtree: true });

        this.prevButton?.addEventListener('click', () => this.#main?.scrollPrev());
        this.nextButton?.addEventListener('click', () => this.#main?.scrollNext());

        this.#desktopQuery.addEventListener('change', this.#onBreakpoint);
        this.#applyMode();
        this.classList.add('is-ready');
      }

      disconnectedCallback() {
        this.#desktopQuery.removeEventListener('change', this.#onBreakpoint);
        this.#unobserveSlides();
        this.#destroyCarousel();
        this.#mediaObserver?.disconnect();
        this.#mediaObserver = null;
      }

      /**
       * Carousel at every width: one main viewer plus a thumbnail rail, which
       * CSS lays out vertically from 990px up and horizontally below. Re-run
       * whenever the breakpoint flips so Embla re-measures after the rail
       * changes orientation.
       *
       * The desktop branch used to swap in a stacked grid of every slide (no
       * carousel, no thumbs). The grid code below -- #observeSlides, the
       * in-place variant swap guarded by `if (!this.#main)`, #revealSlide --
       * is unreachable while this returns the carousel unconditionally, and
       * is left in place so the layout can be restored from one method.
       */
      #applyMode() {
        this.#unobserveSlides();
        this.classList.remove('embla-gallery--grid');
        this.#initCarousel();
        this.#main?.reInit();
      }

      #initCarousel() {
        if (this.#main || typeof EmblaCarousel === 'undefined') return;

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.#main = EmblaCarousel(this.mainViewport, {
          align: 'start',
          skipSnaps: false,
          duration: reducedMotion ? 0 : 22,
        });

        this.#buildThumbs();
        this.#main.on('select', this.#onSelect.bind(this));
        this.#onSelect();
      }

      #destroyCarousel() {
        if (!this.#main) return;
        this.#main.destroy();
        this.#main = null;
        this.#thumbButtons = [];
        if (this.thumbsContainer) this.thumbsContainer.textContent = '';
      }

      /**
       * Grid mode: a stacked gallery can hold several videos, so instead of
       * loading and autoplaying all of them at once, each slide's media is
       * loaded and played when at least half of it is visible and paused
       * again once it leaves the viewport.
       */
      #observeSlides() {
        if (!('IntersectionObserver' in window)) {
          this.#slides().forEach((_, index) => this.#playActiveMedia(index));
          return;
        }
        this.#viewObserver?.disconnect();
        this.#viewObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              const index = this.#slides().indexOf(entry.target);
              if (index < 0) continue;
              if (entry.isIntersecting) {
                this.#playActiveMedia(index);
              } else {
                this.#pauseMedia(entry.target);
              }
            }
          },
          { threshold: 0.5 }
        );
        this.#slides().forEach((slide) => this.#viewObserver.observe(slide));
      }

      #unobserveSlides() {
        this.#viewObserver?.disconnect();
        this.#viewObserver = null;
      }

      /** @param {HTMLElement} slide */
      #pauseMedia(slide) {
        const media = slide.querySelector('deferred-media');
        if (!media) return;
        media.querySelector('video')?.pause();
        media
          .querySelector('iframe.js-youtube')
          ?.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        media.querySelector('iframe.js-vimeo')?.contentWindow?.postMessage('{"method":"pause"}', '*');
      }

      /**
       * Forces gallery videos to loop muted inline. Also patches the media
       * still sitting inside `deferred-media` templates, so the clone that
       * `loadContent()` appends is already muted when it calls `play()` —
       * browsers reject unmuted autoplay, and the rejection would surface as
       * an unhandled promise error.
       */
      #patchEmbeddedMedia() {
        const roots = [this, ...Array.from(this.querySelectorAll('template'), (template) => template.content)];
        for (const root of roots) {
          for (const video of root.querySelectorAll('video')) {
            video.muted = true;
            video.loop = true;
            video.setAttribute('muted', '');
            video.setAttribute('loop', '');
            video.setAttribute('playsinline', '');
            video.setAttribute('autoplay', '');
            video.controls = false;
            video.removeAttribute('controls');
          }
          for (const frame of root.querySelectorAll('iframe[src*="youtube.com"], iframe[src*="vimeo.com"]')) {
            if (frame.dataset.mediaPatched) continue;
            frame.dataset.mediaPatched = 'true';
            const url = new URL(frame.src);
            url.searchParams.set(url.hostname.includes('vimeo') ? 'muted' : 'mute', '1');
            url.searchParams.set('loop', '1');
            frame.src = url.toString();
          }
        }
      }

      /**
       * Autoplays the video on the selected slide without a poster tap: loads
       * the deferred-media on first visit, then (re)starts playback. Other
       * slides' media were already paused by `pauseAllMedia()` in `#onSelect`.
       * @param {number} index - currently selected slide index
       */
      #playActiveMedia(index) {
        const media = this.#slides()[index]?.querySelector('deferred-media');
        if (!media) return;
        if (!media.getAttribute('loaded')) media.loadContent(false);

        media.querySelector('video')?.play().catch(() => {});
        media
          .querySelector('iframe.js-youtube')
          ?.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
        media.querySelector('iframe.js-vimeo')?.contentWindow?.postMessage('{"method":"play"}', '*');
      }

      /**
       * Contract used by product-info.js after a variant change. Variant
       * images are not part of this gallery, so the id it passes is normally
       * unknown here and the call is a no-op: the gallery keeps its slides,
       * thumbs and current position. Thumbs are only rebuilt when the slide
       * list actually changed.
       * @param {string} mediaId - `${sectionId}-${media.id}` of the new featured media
       */
      setActiveMedia(mediaId) {
        if (!this.#main) return; // grid mode: everything is already visible
        const slides = this.#slides();
        if (slides.length !== this.#thumbButtons.length) {
          this.#buildThumbs();
          this.#main.reInit();
        }
        const index = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (index < 0) return;
        this.#main.scrollTo(index, true);
        this.#onSelect();
      }

      #slides() {
        return Array.from(this.querySelectorAll('li[data-media-id]'));
      }

      /**
       * Swaps in the slide rendered for the newly selected variant using the
       * gallery's own slide motion: the incoming slide is inserted right
       * after the current variant slide, Embla animates to it exactly like a
       * "next" swipe, and the outgoing slide is dropped once the scroll has
       * settled. The incoming image is decoded first so the motion never
       * reveals a blank frame; the thumb rail is updated in place.
       * Bundles render one variant slide per component product; the attribute
       * value keys them so each slide swaps independently.
       * @param {HTMLElement} newSlide - `li[data-variant-slide]` from the fetched section HTML
       */
      async updateVariantSlide(newSlide) {
        const key = newSlide.dataset.variantSlide ?? '';
        const outgoing = this.querySelector(`li[data-variant-slide="${key}"]`);
        if (!outgoing) return;

        if (outgoing.dataset.variantMediaId === newSlide.dataset.variantMediaId) {
          this.#main?.scrollTo(this.#slides().indexOf(outgoing));
          return;
        }

        // Grid mode: the slide sits in a static grid, so swap it in place
        // once the new image has decoded and let CSS cross-fade it.
        if (!this.#main) {
          const token = Symbol('variant-swap');
          this.#variantSwaps.set(key, token);
          const incoming = document.importNode(newSlide, true);
          const image = incoming.querySelector('img');
          if (image) {
            image.loading = 'eager';
            await Promise.race([image.decode().catch(() => {}), new Promise((resolve) => setTimeout(resolve, 1500))]);
          }
          if (this.#variantSwaps.get(key) !== token || !this.isConnected || !outgoing.isConnected) return;
          incoming.classList.add('is-entering');
          this.#viewObserver?.unobserve(outgoing);
          outgoing.replaceWith(incoming);
          requestAnimationFrame(() => incoming.classList.remove('is-entering'));
          this.#viewObserver?.observe(incoming);
          this.#revealSlide(incoming);
          return;
        }

        const token = Symbol('variant-swap');
        this.#variantSwaps.set(key, token);

        const incoming = document.importNode(newSlide, true);
        const image = incoming.querySelector('img');
        if (image) {
          image.loading = 'eager';
          await Promise.race([image.decode().catch(() => {}), new Promise((resolve) => setTimeout(resolve, 1500))]);
        }
        if (this.#variantSwaps.get(key) !== token || !this.isConnected || !outgoing.isConnected) return;

        const slideIndex = this.#slides().indexOf(outgoing);
        const current = this.#main.selectedScrollSnap();

        // The outgoing slide stays in the track only for the duration of the
        // motion; strip its identity so lookups resolve to the incoming one.
        delete outgoing.dataset.variantSlide;
        outgoing.dataset.mediaId = `${outgoing.dataset.mediaId}-outgoing`;
        outgoing.dataset.outgoing = '';
        outgoing.after(incoming);

        const thumbImage = this.#thumbButtons[slideIndex]?.querySelector('img');
        if (thumbImage) thumbImage.src = incoming.dataset.thumbSrc || '';

        // Re-measure with the extra slide, keep the visible slide where it is
        // (its index shifted by one if it sat after the insertion point), then
        // animate to the incoming slide.
        this.#main.reInit();
        this.#main.scrollTo(current > slideIndex ? current + 1 : current, true);
        this.#main.scrollTo(slideIndex + 1);

        let finished = false;
        const finish = () => {
          if (finished) return;
          finished = true;
          clearTimeout(fallback);
          this.#main?.off('settle', finish);
          outgoing.remove();
          if (!this.#main) return;
          this.#main.reInit();
          // Removing the slide before the incoming one shifts it back to
          // slideIndex; jump there so nothing moves on screen.
          this.#main.scrollTo(slideIndex, true);
          this.#onSelect();
        };
        const fallback = setTimeout(finish, 900);
        this.#main.on('settle', finish);
      }

      /**
       * Grid mode: a swapped slide further down the stack (e.g. the second
       * component of a bundle) would change out of sight, so scroll the page
       * until the slide is fully visible under the sticky header. Slides that
       * are already on screen are left alone.
       * @param {HTMLElement} slide
       */
      #revealSlide(slide) {
        const rect = slide.getBoundingClientRect();
        const header = document.querySelector('.section-header');
        const headerHeight = header ? header.getBoundingClientRect().height : 0;
        const viewportHeight = window.innerHeight;
        const fitsViewport = rect.height <= viewportHeight - headerHeight;
        const fullyVisible = rect.top >= headerHeight && rect.bottom <= viewportHeight;
        const mostlyVisible = rect.top < viewportHeight * 0.5 && rect.bottom > viewportHeight * 0.5;
        if (fullyVisible || (!fitsViewport && mostlyVisible)) return;

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const target = fitsViewport
          ? window.scrollY + rect.top - headerHeight - (viewportHeight - headerHeight - rect.height) / 2
          : window.scrollY + rect.top - headerHeight;
        window.scrollTo({ top: Math.max(0, target), behavior: reducedMotion ? 'auto' : 'smooth' });
      }

      #buildThumbs() {
        if (!this.thumbsContainer) return;

        const labelTemplate = this.dataset.thumbLabel || 'INDEX';
        this.thumbsContainer.textContent = '';
        this.#thumbButtons = this.#slides().map((slide, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'embla-gallery__thumb';
          if (slide.hasAttribute('data-thumb-video')) button.classList.add('embla-gallery__thumb--video');
          button.setAttribute('aria-label', labelTemplate.replace('INDEX', String(index + 1)));

          const image = document.createElement('img');
          image.src = slide.dataset.thumbSrc || '';
          image.alt = '';
          image.loading = 'lazy';
          button.append(image);

          button.addEventListener('click', () => this.#main?.scrollTo(index));
          this.thumbsContainer.append(button);
          return button;
        });
      }

      /** Centers the active thumb in the rail so neighboring thumbs stay visible. */
      #revealThumb(button) {
        const viewport = this.thumbsViewport;
        if (!viewport || !button) return;

        if (this.#desktopQuery.matches) {
          viewport.scrollTo({
            top: button.offsetTop - (viewport.clientHeight - button.offsetHeight) / 2,
            behavior: 'smooth',
          });
        } else {
          viewport.scrollTo({
            left: button.offsetLeft - (viewport.clientWidth - button.offsetWidth) / 2,
            behavior: 'smooth',
          });
        }
      }

      #onSelect() {
        if (!this.#main) return;
        const index = this.#main.selectedScrollSnap();

        // Decode the neighbouring slides' images ahead of time. A lazy image
        // that is still undecoded when the user swipes forces a synchronous
        // decode on the main thread mid-transition, which stutters the slide.
        this.#prefetchNeighbours(index);

        // Thumbs mirror the slides minus any outgoing variant slide that is
        // still in the track mid-swap, so the highlight never hops to the
        // neighbour while the new variant slides in.
        const slides = this.#slides();
        const visible = slides.filter((slide) => !slide.hasAttribute('data-outgoing'));
        const selected = slides[index];
        slides.forEach((slide) => slide.toggleAttribute('data-selected', slide === selected));
        let thumbIndex = visible.indexOf(selected);
        if (thumbIndex < 0) thumbIndex = Math.max(0, visible.indexOf(selected?.nextElementSibling));
        const total = visible.length;

        this.#thumbButtons.forEach((button, i) => {
          if (i === thumbIndex) {
            button.setAttribute('aria-current', 'true');
          } else {
            button.removeAttribute('aria-current');
          }
        });
        this.#revealThumb(this.#thumbButtons[thumbIndex]);

        if (this.prevButton) this.prevButton.disabled = !this.#main.canScrollPrev();
        if (this.nextButton) this.nextButton.disabled = !this.#main.canScrollNext();
        if (this.status) this.status.textContent = `${thumbIndex + 1} / ${total}`;

        window.pauseAllMedia?.();
        this.#playActiveMedia(index);
      }

      /**
       * Eagerly decode the images on the slides adjacent to `index` so the
       * next swipe composites an already-rastered frame instead of blocking
       * on a synchronous decode. Best-effort — decode() rejections are ignored.
       * @param {number} index - currently selected slide index
       */
      #prefetchNeighbours(index) {
        const slides = this.#slides();
        for (const offset of [-1, 1]) {
          const neighbour = slides[index + offset];
          if (!neighbour) continue;
          const img = neighbour.querySelector('img[loading="lazy"]');
          if (!img || img.dataset.prefetched) continue;
          img.dataset.prefetched = 'true';
          img.loading = 'eager';
          img.decode?.().catch(() => {});
        }
      }
    }
  );
}
